import { useCallback, type ReactNode } from 'react'
import { emit } from '../../platform/analytics/analytics'
import { useOnce } from '../../platform/analytics/useOnce'
import { Async, type AsyncState } from '../../platform/async/Async'
import { ErrorBoundary } from '../../platform/async/ErrorBoundary'
import { Skeleton } from '../../platform/async/Skeleton'
import { PRIMITIVES, type PrimitiveEntry } from '../../platform/primitives/registry'
import './statesReference.css'

/**
 * Every primitive in all five states — Task 19 (#34).
 *
 * The visual-regression surface and the design-system contract. A primitive
 * missing a state is a defect caught by a test rather than in production.
 *
 * Each cell is wrapped in its own `ErrorBoundary` (#95): this page renders
 * every primitive at once, so an invariant violation in one of them would
 * otherwise blank the whole reference — the one page where a failure most needs
 * to be visible and contained rather than fatal.
 */

const STATES = ['loading', 'empty', 'stale', 'error', 'success'] as const

/**
 * A fixed instant, not `new Date()` — and midday UTC, not midnight.
 *
 * Fixed, because the stale state prints its date and a live clock would make
 * every screenshot diff noise.
 *
 * Midday, because `Async` formats `asOf` in the VIEWER's timezone, which is its
 * deliberate choice: "showing data from" means the date it was on where the
 * reader is. At midnight UTC this page rendered "24 Jul" in Vancouver and
 * "25 Jul" in CI — a reference surface that disagrees with itself by machine.
 * 12:00Z lands on the same calendar day from UTC-11 to UTC+12, which is every
 * timezone this app will be read in.
 */
export const AS_OF = new Date('2026-07-25T12:00:00Z')

function stateFor(name: (typeof STATES)[number], node: ReactNode): AsyncState<ReactNode> {
  switch (name) {
    case 'loading':
      return { status: 'loading' }
    case 'empty':
      return { status: 'empty' }
    case 'stale':
      return { status: 'stale', data: node, asOf: AS_OF }
    case 'error':
      // Specific, per CLAUDE.md: names whose fault and the recovery path.
      // "Something went wrong" is the defect this page exists to demonstrate
      // the absence of.
      return {
        status: 'error',
        message: "Royal Bank hasn't synced since Tuesday — reconnect to see the last 4 days",
        recovery: { label: 'Reconnect', onAct: () => {} },
      }
    case 'success':
      return { status: 'success', data: node }
  }
}

/**
 * One cell, rendered INSIDE its boundary.
 *
 * `p.render()` used to be evaluated as an argument to `stateFor(...)` — during
 * `StatesReference`'s own render, before the `ErrorBoundary` element even
 * existed. So a component throwing during render was contained (that is the
 * real case: `Money` on bad cents), but a registry entry whose `render` threw
 * took the whole page down. A registry entry is code too.
 *
 * Calling it here moves the call inside the boundary's subtree, where both
 * cases are contained.
 */
function Cell({ entry, state }: { entry: PrimitiveEntry; state: (typeof STATES)[number] }) {
  // Only `stale` and `success` display the node, so only they build it. Cheaper,
  // and it narrows the blast radius: a registry entry that throws takes out the
  // two cells that would have shown it rather than all five — which is what
  // web-tester measured for a component-level throw (2 failed, 38 healthy) and
  // is the behaviour worth matching.
  const needsNode = state === 'stale' || state === 'success'
  return (
    <Async
      surface="states-reference"
      state={stateFor(state, needsNode ? entry.render() : null)}
      skeleton={<Skeleton height={64} />}
      empty={{ title: 'Nothing here yet', action: { label: 'Add one', onAct: () => {} } }}
    >
      {(node) => <>{node}</>}
    </Async>
  )
}

export function StatesReference() {
  // Once per mount, StrictMode-safe: see useOnce.
  useOnce(useCallback(() => emit('states_reference_viewed'), []))
  return (
    <div className="states">
      {/* No <h1>: ChromeLayout renders the page name as the document heading
          (#32), and a second one here would give the page two headings with the
          same text. The sections below are h2, which is where route content
          starts. */}
      <p className="states__intro">
        Every primitive in all five states. This page is the visual-regression surface — a
        primitive missing a state is a defect, caught by a test rather than by production.
      </p>
      {/* #126. The five columns are ASYNC's states, and the coverage test can
          only enforce those. A primitive's own internal states — the grid's
          empty, which means "the filter matched nothing" rather than "no data"
          — are not Async's to enumerate, so they appear as named scenarios
          instead. Stated here rather than only in the registry, because a
          reader seeing a ninth section headed "DataGrid — no matching rows"
          has no other way to tell it is a second instance of an existing
          primitive rather than a primitive of its own. */}
      {/* Derived, not unconditional: if the scenario entry is ever removed, an
          explanation of a marker that appears nowhere is worse than no
          explanation. Same instinct as the coverage check reading the glob
          rather than a hard-coded set. */}
      {PRIMITIVES.some((p) => p.of) && (
        <p className="states__intro">
          Sections marked <em>a scenario of</em> are a second instance of a primitive already
          listed, covering a state of its own that Async's five cannot reach.
        </p>
      )}

      {PRIMITIVES.map((p) => (
        <section key={p.name} className="states__section">
          {/* The marker sits BESIDE the heading, not inside it. Inside, it
              becomes part of the h2's accessible name — "DataGrid — no matching
              rows a scenario of DataGrid" — which is what a screen-reader user
              hears when navigating by heading, and what every `getByRole(
              'heading', { name })` lookup on this page matches against. A
              heading names its section; this is metadata about the section. */}
          <div className="states__head">
            <h2 className="states__name">{p.name}</h2>
            {p.of && <p className="states__of">a scenario of {p.of}</p>}
          </div>
          <div className="states__grid">
            {STATES.map((s) => (
              <div key={s} data-state={s} className="states__cell">
                <span className="states__label">{s}</span>
                <ErrorBoundary name={`${p.name} (${s})`}>
                  <Cell entry={p} state={s} />
                </ErrorBoundary>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
